import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { TextInput } from "../../components/textInput";
import { ImageInput } from "../../components/imageInput";
import { Button } from "../../components/button";
import { Toast } from "../../components/toast";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import classNames from "../../utils/classNames";
import { PageHeading } from "../../components/pageHeading";

const schema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address").min(1, "Email address is required"),
  phone_number: z.string().optional(),
});

const THEME_OPTIONS = [
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "system", label: "System" },
];

function Profile() {
  const { authContext, fetchUserData } = useContext(AuthContext);
  const { theme, setTheme } = useContext(ThemeContext);
  const authFetch = useAuthFetch();
  const userId = authContext.user.userId;
  const authId = authContext.user.authId;
  const authOrgId = authContext.organization.authOrgId;
  const orgId = authContext.organization.orgId;

  // Pre-populate from AuthContext immediately — no waiting for API
  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      first_name: authContext.user.firstName || "",
      last_name: authContext.user.lastName || "",
      email: authContext.user.email || "",
      phone_number: authContext.user.phoneNumber || "",
    },
    resolver: zodResolver(schema),
  });

  const { handleSubmit, setValue } = methods;

  // Fetch full user record in background to get customers + roles for submit passthrough
  const [existingData, setExistingData] = useState({
    customers: [],
    roles: [],
    profile_url: authContext.user.imageUrl || "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [themePreference, setThemePreference] = useState(authContext.user.themePreference || "system");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  useEffect(() => {
    if (!userId || !authOrgId) return;

    authFetch(`${process.env.REACT_APP_API_URL}/v1/users/${userId}?auth_org_id=${authOrgId}&detail=detailed`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch profile");
        return res.json();
      })
      .then((data) => {
        setValue("first_name", data.first_name || "");
        setValue("last_name", data.last_name || "");
        setValue("email", data.email || "");
        setValue("phone_number", data.phone_number || "");

        if (data.theme_preference) {
          setThemePreference(data.theme_preference);
        }

        const parsedCustomers = data.customers?.[0]?.name ? data.customers : [];
        setExistingData({
          customers: parsedCustomers.map((c) => c.id),
          roles: data.roles || [],
          profile_url: data.profile_url || "",
        });
      })
      .catch((err) => {
        console.error("Error fetching profile:", err);
      });
  }, [userId, authOrgId, setValue]);

  const handleThemeChange = (value) => {
    setThemePreference(value);
    setTheme(value);
  };

  const onSubmit = (data) => {
    const formData = new FormData();
    formData.append("first_name", data.first_name);
    formData.append("last_name", data.last_name);
    formData.append("email", data.email);
    formData.append("phone_number", data.phone_number || "");
    formData.append("theme_preference", themePreference);

    existingData.customers.forEach((id, i) => formData.append(`customers[${i}]`, id));
    existingData.roles.forEach((role, i) => formData.append(`roles[${i}]`, role));

    formData.append("org_id", orgId);
    formData.append("auth_org_id", authOrgId);
    formData.append("auth_id", authId);

    if (imageFile) {
      formData.append("file", imageFile);
    }

    authFetch(`${process.env.REACT_APP_API_URL}/v1/users/${userId}`, {
      method: "PUT",
      body: formData,
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to update profile");
        return res.json();
      })
      .then(() => {
        setToast({ show: true, message: "Profile updated successfully", type: "success" });
        fetchUserData();
      })
      .catch(() => {
        setToast({ show: true, message: "Failed to update profile", type: "danger" });
      });
  };

  const onError = (errors) => console.log("form error", errors);

  return (
    <div className="py-6 dark:text-gray-100">
      <div className="px-4 sm:px-6 lg:px-6">
        <PageHeading title="Profile" />
        <FormProvider {...methods}>
          <div className="space-y-8 max-w-md">
            <TextInput name="first_name" label="First name" />
            <TextInput name="last_name" label="Last name" />
            <TextInput name="email" label="Email address" />
            <TextInput name="phone_number" label="Phone number" />
            <ImageInput
              name="profileImage"
              label="Profile photo"
              onChange={(e) => setImageFile(e.target.files[0] || null)}
              accept="image/png, image/jpeg, image/gif, image/svg+xml, image/webp"
              maxFileSize={3201010}
              existing={existingData.profile_url}
            />

            <div>
              <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Theme
              </p>
              <div className="inline-flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
                {THEME_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleThemeChange(option.value)}
                    className={classNames(
                      themePreference === option.value
                        ? "bg-brand-primary text-white font-semibold"
                        : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-white/[0.06]",
                      "px-4 py-2 text-sm transition-colors duration-150 border-r border-gray-200 dark:border-gray-700 last:border-r-0"
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex">
              <Button onClick={handleSubmit(onSubmit, onError)}>Save</Button>
            </div>
          </div>
        </FormProvider>
      </div>

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onHide={() => setToast((prev) => ({ ...prev, show: false }))}
      />
    </div>
  );
}

export { Profile };
